/**
 * What a customer has paid, split across the parts of a Flight + Hotel package.
 *
 * A package is financed as ONE plan: the deposit and every installment cover
 * the flight, the hotel and Defera's fees together. So "what have I paid
 * toward the hotel?" has no stored answer — the money was never earmarked.
 * The honest answer is a proportional one: each part's share of the package
 * total, applied to what has actually been collected.
 *
 * Built for the cancel-for-credit overlay, where a customer looking at one
 * number ("$331.10 back as Defera Credit") on a trip that includes a hotel
 * cannot tell whether the hotel is part of what comes back. It is.
 */

/** Largest-remainder rounding: every cent of `paidCents` lands on some line,
 *  so the breakdown always sums to the figure printed above it. */
function distribute(paidCents, weights) {
  const total = weights.reduce((sum, w) => sum + w, 0);
  if (total <= 0) return weights.map(() => 0);
  const exact = weights.map((w) => (paidCents * w) / total);
  const floors = exact.map((v) => Math.floor(v));
  let remainder = paidCents - floors.reduce((sum, v) => sum + v, 0);
  // Hand the leftover cents to the largest fractional parts first.
  const order = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  const out = [...floors];
  for (let k = 0; remainder > 0 && k < order.length; k += 1, remainder -= 1) {
    out[order[k].i] += 1;
  }
  return out;
}

/**
 * @returns {{flightCents:number, stayCents:number, feeCents:number}|null}
 *   null when there is nothing worth splitting — no hotel on the trip, nothing
 *   paid yet, or no component amounts to weight by. Callers render the plain
 *   total in that case.
 */
export function splitPaidAcrossPackage({ paidCents, flightCents, stayCents, feeCents = 0 } = {}) {
  const paid = Math.round(Number(paidCents));
  const flight = Math.max(0, Math.round(Number(flightCents) || 0));
  const stay = Math.max(0, Math.round(Number(stayCents) || 0));
  const fee = Math.max(0, Math.round(Number(feeCents) || 0));
  if (!Number.isFinite(paid) || paid <= 0) return null;
  // No hotel: the cancel overlay already says what the whole figure is for.
  if (stay <= 0) return null;
  if (flight + stay + fee <= 0) return null;
  const [flightPaid, stayPaid, feePaid] = distribute(paid, [flight, stay, fee]);
  return { flightCents: flightPaid, stayCents: stayPaid, feeCents: feePaid };
}
